import React, {ReactNode} from 'react'
import {css} from '@emotion/react'
import styled from '@emotion/styled'
import {toggle} from 'opticks'
import {ThemeType} from 'types/Theme'

import {SkeletonLoader} from '../SkeletonLoader'
import {Text} from '../Text'

interface Props {
  /** Pass through classname to allow styles overrides */
  className?: string
  /** The label to be shown next to the badge */
  label?: ReactNode
  /** The sub label shown below the label */
  subLabel?: ReactNode
  /** The number to be shown in the badge */
  value: number
  /** Use slightly thinner font for label */
  alternativeLabelStyles?: boolean
  /** Wether the subLabel goes below or in the same line as the label */
  singleLineLabels?: boolean
  scoreId?: string
  scoreLabelId?: string
  scoreSubLabelId?: string
}

interface ScoreProps {
  id?: string
  value: number
  isSingleLine?: boolean
}

const BADGE_SIZE = 32
const SINGLE_LINE_BADGE_SIZE = 24
const BADGE_RADIUS = 20
const BADGE_RADIUS_TAIL = 4

const RatingElement = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

export const BadgeWrapper = styled.div<{
  value: number
  isSingleLine: boolean
}>(({theme, value, isSingleLine}) => {
  const isNewDesign = toggle('b4f4f3cc-website-restyle-v3', false, true)
  return css`
    ${isSingleLine
      ? theme.typography.text.labelXS
      : theme.typography.text.labelS}
    width: ${isSingleLine ? SINGLE_LINE_BADGE_SIZE : BADGE_SIZE}px;
    line-height: ${isSingleLine ? SINGLE_LINE_BADGE_SIZE : BADGE_SIZE}px;
    text-align: center;
    color: ${badgeColor(theme, value, isNewDesign)};
    background-color: ${badgeBg(theme, value, isNewDesign)};
    border-radius: ${BADGE_RADIUS}px ${BADGE_RADIUS}px ${BADGE_RADIUS_TAIL}px
      ${BADGE_RADIUS}px;
  `
})

export const LabelWrapper = styled.div<{isSingleLine: boolean}>(
  ({isSingleLine}) => css`
    display: flex;
    flex-direction: ${isSingleLine ? 'row' : 'column'};
    align-items: ${isSingleLine ? 'center' : 'flex-start'};
    justify-content: space-around;
  `
)

export const LabelText = styled(Text)<{isSingleLine: boolean}>(
  ({theme, isSingleLine}) => css`
    display: flex;
    align-items: center;
    color: ${theme.colors.content.neutral.c950};
    margin-left: ${isSingleLine
      ? theme.layout.spacing.s200
      : theme.layout.spacing.s300}px;
  `
)

export const SubLabelText = styled(Text)<{isSingleLine: boolean}>(
  ({theme, isSingleLine}) => css`
    display: flex;
    align-items: center;
    color: ${theme.colors.content.neutral.c600};
    margin-left: ${isSingleLine
      ? theme.layout.spacing.s200
      : theme.layout.spacing.s300}px;
  `
)

const badgeColor = (
  theme: ThemeType,
  value: number,
  isNewDesign?: boolean // Added as part of b4f4f3cc-website-restyle-v3
) => {
  if (isNewDesign && value >= 7) return theme.colors.content.neutral.c000
  if (isNewDesign && value < 7) return theme.colors.content.neutral.c950
  if (value >= 9.5) return theme.colors.qualityScale.exceptional.content
  if (value >= 9) return theme.colors.qualityScale.excellent.content
  if (value >= 8.5) return theme.colors.qualityScale.veryNice.content
  if (value >= 8) return theme.colors.qualityScale.nice.content
  if (value >= 7) return theme.colors.qualityScale.good.content
  if (value >= 6) return theme.colors.qualityScale.average.content
  if (value > 0) return theme.colors.qualityScale.poor.content
  return theme.colors.qualityScale.default.content
}

const badgeBg = (
  theme: ThemeType,
  value: number,
  isNewDesign?: boolean // Added as part of b4f4f3cc-website-restyle-v3
) => {
  if (isNewDesign && value >= 7) return '#00A153'
  if (isNewDesign && value < 7) return theme.colors.background.neutral.c100
  if (value >= 9.5) return theme.colors.qualityScale.exceptional.background
  if (value >= 9) return theme.colors.qualityScale.excellent.background
  if (value >= 8.5) return theme.colors.qualityScale.veryNice.background
  if (value >= 8) return theme.colors.qualityScale.nice.background
  if (value >= 7) return theme.colors.qualityScale.good.background
  if (value >= 6) return theme.colors.qualityScale.average.background
  if (value > 0) return theme.colors.qualityScale.poor.background
  return theme.colors.qualityScale.default.background
}

const Score = ({value, isSingleLine = false, id}: ScoreProps) => (
  <BadgeWrapper isSingleLine={isSingleLine} value={value} id={id}>
    {value}
  </BadgeWrapper>
)

export const ScoreRatingLoader = () => (
  <div>
    <SkeletonLoader height={28} width={28} marginTop={0} />
  </div>
)

export const ScoreRating = ({
  label,
  subLabel,
  value,
  alternativeLabelStyles,
  className,
  singleLineLabels = false,
  scoreId,
  scoreLabelId,
  scoreSubLabelId
}: Props) => (
  <RatingElement className={className}>
    <div>
      <Score id={scoreId} isSingleLine={singleLineLabels} value={value} />
    </div>
    <LabelWrapper isSingleLine={singleLineLabels}>
      {label && (
        <LabelText
          id={scoreLabelId}
          variant={
            alternativeLabelStyles || singleLineLabels ? 'bodyS' : 'labelS'
          }
          isSingleLine={singleLineLabels}
        >
          {label}
        </LabelText>
      )}
      {subLabel && (
        <SubLabelText
          as="div"
          isSingleLine={singleLineLabels}
          variant={singleLineLabels ? 'bodyS' : 'bodyXS'}
          id={scoreSubLabelId}
        >
          {subLabel}
        </SubLabelText>
      )}
    </LabelWrapper>
  </RatingElement>
)
